/**
 * Activity Filter — client-side category filtering + pagination
 *
 * The activities archive renders every published activity into the DOM.
 * This module hides cards that don't match the selected categories,
 * paginates whatever is left, and renders prev/next/numbered controls.
 */

class ActivityFilter {
	constructor() {
		this.filterForm  = document.getElementById( 'activity-filter-form' );
		this.grid        = document.querySelector( '.activity-grid' );
		this.pagination  = document.querySelector( '.activity-pagination' );
		this.checkboxes  = Array.from( document.querySelectorAll( '.category-filter' ) );
		this.allCards    = Array.from( document.querySelectorAll( '.activity-card' ) );
		this.clearButton = document.querySelector( '.clear-filters' );
		this.resultsCount = document.querySelector( '.results-count' );
		this.resultsHeading = document.querySelector( '.activity-results-heading' );

		// Bail if we're not on the activities archive.
		if ( ! this.grid || this.allCards.length === 0 ) {
			return;
		}

		this.pageSize    = parseInt( this.grid.dataset.pageSize, 10 ) || 12;
		this.currentPage = 1;
		this.matched     = this.allCards.slice();

		this.init();
	}

	init() {
		this.loadStateFromURL();

		this.checkboxes.forEach( ( checkbox ) => {
			checkbox.addEventListener( 'change', () => {
				this.currentPage = 1;
				this.apply( true );
			} );
		} );

		if ( this.clearButton ) {
			this.clearButton.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				this.clear();
			} );
		}

		// Browser back/forward should restore filter state.
		window.addEventListener( 'popstate', () => {
			this.loadStateFromURL();
			this.apply( false );
		} );

		this.apply( false );
	}

	loadStateFromURL() {
		const params = new URLSearchParams( window.location.search );

		const categories = params.get( 'categories' );
		const selected   = categories ? categories.split( ',' ).filter( Boolean ) : [];

		this.checkboxes.forEach( ( checkbox ) => {
			// The empty-valued "All Activities" radio is checked when nothing
			// else is selected; category radios match against the URL value.
			checkbox.checked = checkbox.value
				? selected.includes( checkbox.value )
				: selected.length === 0;
		} );

		const pageParam = parseInt( params.get( 'apage' ), 10 );
		this.currentPage = pageParam > 0 ? pageParam : 1;
	}

	getSelectedCategoryIds() {
		// The "All Activities" radio has an empty value, so filter it out —
		// an empty selection means "show everything".
		return this.checkboxes
			.filter( ( cb ) => cb.checked && cb.value )
			.map( ( cb ) => cb.value );
	}

	apply( pushState ) {
		const selected = this.getSelectedCategoryIds();

		// Match cards against the selected categories.
		if ( selected.length === 0 ) {
			this.matched = this.allCards.slice();
		} else {
			this.matched = this.allCards.filter( ( card ) => {
				const cardCats = ( card.dataset.categories || '' )
					.split( ',' )
					.filter( Boolean );
				return selected.some( ( id ) => cardCats.includes( id ) );
			} );
		}

		// Clamp the requested page to the new range.
		const totalPages = Math.max( 1, Math.ceil( this.matched.length / this.pageSize ) );
		if ( this.currentPage > totalPages ) {
			this.currentPage = totalPages;
		}

		this.render();
		this.updateClearButton( selected );
		this.updateResultsCount();
		this.updateResultsHeading();

		if ( pushState ) {
			this.updateURL( selected );
		}
	}

	render() {
		const start = ( this.currentPage - 1 ) * this.pageSize;
		const end   = start + this.pageSize;
		const visibleSet = new Set( this.matched.slice( start, end ) );

		this.allCards.forEach( ( card ) => {
			card.style.display = visibleSet.has( card ) ? '' : 'none';
		} );

		this.renderPagination();
	}

	renderPagination() {
		if ( ! this.pagination ) {
			return;
		}

		const totalPages = Math.ceil( this.matched.length / this.pageSize );
		this.pagination.innerHTML = '';

		if ( totalPages <= 1 ) {
			return;
		}

		const list = document.createElement( 'ul' );
		list.className = 'activity-pagination__list';

		// Prev
		list.appendChild( this.makePageButton( {
			label: 'Previous',
			page: this.currentPage - 1,
			disabled: this.currentPage === 1,
			extraClass: 'activity-pagination__nav activity-pagination__nav--prev',
		} ) );

		// Page numbers (with truncation when there are many pages)
		const numbers = this.buildPageNumbers( totalPages );
		numbers.forEach( ( entry ) => {
			if ( entry === '…' ) {
				const li = document.createElement( 'li' );
				li.className = 'activity-pagination__ellipsis';
				li.textContent = '…';
				list.appendChild( li );
			} else {
				list.appendChild( this.makePageButton( {
					label: String( entry ),
					page: entry,
					current: entry === this.currentPage,
					extraClass: 'activity-pagination__page',
				} ) );
			}
		} );

		// Next
		list.appendChild( this.makePageButton( {
			label: 'Next',
			page: this.currentPage + 1,
			disabled: this.currentPage === totalPages,
			extraClass: 'activity-pagination__nav activity-pagination__nav--next',
		} ) );

		this.pagination.appendChild( list );
	}

	buildPageNumbers( totalPages ) {
		// Always show first/last; show 2 around current.
		const pages = new Set( [ 1, totalPages ] );
		for ( let i = this.currentPage - 1; i <= this.currentPage + 1; i++ ) {
			if ( i >= 1 && i <= totalPages ) {
				pages.add( i );
			}
		}

		const sorted = Array.from( pages ).sort( ( a, b ) => a - b );
		const result = [];
		let prev = 0;

		sorted.forEach( ( p ) => {
			if ( p - prev > 1 ) {
				result.push( '…' );
			}
			result.push( p );
			prev = p;
		} );

		return result;
	}

	makePageButton( { label, page, current = false, disabled = false, extraClass = '' } ) {
		const li = document.createElement( 'li' );

		const button = document.createElement( 'button' );
		button.type = 'button';
		button.textContent = label;
		button.className = `activity-pagination__button ${ extraClass }`.trim();

		if ( current ) {
			button.classList.add( 'is-current' );
			button.setAttribute( 'aria-current', 'page' );
		}

		if ( disabled ) {
			button.disabled = true;
			button.setAttribute( 'aria-disabled', 'true' );
		} else {
			button.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				this.currentPage = page;
				this.apply( true );
				this.scrollToTop();
			} );
		}

		li.appendChild( button );
		return li;
	}

	scrollToTop() {
		if ( ! this.grid ) {
			return;
		}
		const top = this.grid.getBoundingClientRect().top + window.pageYOffset - 24;
		window.scrollTo( { top, behavior: 'smooth' } );
	}

	clear() {
		// Reset to the "All Activities" radio (empty value) rather than an
		// empty radio group so a selection is always visible.
		this.checkboxes.forEach( ( cb ) => {
			cb.checked = ! cb.value;
		} );
		this.currentPage = 1;
		this.apply( true );
	}

	updateClearButton( selected ) {
		if ( ! this.clearButton ) {
			return;
		}
		this.clearButton.style.display = selected.length > 0 ? 'inline-flex' : 'none';
	}

	updateResultsCount() {
		if ( ! this.resultsCount ) {
			return;
		}
		const total = this.matched.length;
		const noun  = total === 1 ? 'activity' : 'activities';

		if ( total === this.allCards.length ) {
			this.resultsCount.textContent = `${ total } ${ noun }`;
		} else {
			this.resultsCount.textContent = `${ total } of ${ this.allCards.length } ${ noun }`;
		}
	}

	updateResultsHeading() {
		if ( ! this.resultsHeading ) {
			return;
		}

		const selected = this.checkboxes.find( ( cb ) => cb.checked );

		if ( selected && selected.dataset.name ) {
			this.resultsHeading.textContent = `Activities in ${ selected.dataset.name }`;
		} else {
			this.resultsHeading.textContent = 'All Activities';
		}
	}

	updateURL( selected ) {
		const url = new URL( window.location );

		if ( selected.length > 0 ) {
			url.searchParams.set( 'categories', selected.join( ',' ) );
		} else {
			url.searchParams.delete( 'categories' );
		}

		if ( this.currentPage > 1 ) {
			url.searchParams.set( 'apage', String( this.currentPage ) );
		} else {
			url.searchParams.delete( 'apage' );
		}

		window.history.pushState( {}, '', url );
	}
}

export default ActivityFilter;
